import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useNDVR1500Stocks } from '@/hooks/market-data-queries';
import { themeDefault } from 'styles';
import { sanitizeText } from 'utils';

import { Card } from '../../Card';
import { IconClose } from '../../icon';
import { HorizontalLayout, VerticalLayout } from '../../Layout';
import { Text } from '../../Text';
import { FormLabel, InputDescription } from './FormCommons';
import { Input } from './Input';

const ITEM_HEIGHT = 24;

export const TickerPicker = ({ name, label }: { name: string; label: string }) => {
    const {
        setValue,
        formState: { errors },
        watch,
    } = useFormContext();
    const error = get(errors, name);

    const selectedTickers: string[] = watch(name, []);
    const setSelectedTickers = useCallback(
        (tickers: string[]) => {
            setValue(name, tickers, {
                shouldDirty: true,
                shouldValidate: true,
            });
        },
        [name, setValue],
    );

    const { data } = useNDVR1500Stocks();
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(1);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { x, y, refs, strategy, context } = useFloating({
        placement: 'bottom-start',
        strategy: 'fixed',
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });
    useInteractions([
        useDismiss(context, {
            escapeKey: false,
        }),
    ]);

    const matches = useMemo(() => {
        const regex = new RegExp(search, 'i');
        return (data || [])
            ?.filter(
                (stock) =>
                    (regex.test(stock.tickerSymbol) || regex.test(stock.companyName)) &&
                    !selectedTickers?.includes(stock.tickerSymbol),
            )
            .slice(0, 50);
    }, [data, search, selectedTickers]);

    const addTicker = useCallback(
        (symbol: string) => {
            setSelectedTickers([...(selectedTickers || []), symbol]);
            setSearch('');
            inputRef.current?.focus();
        },
        [selectedTickers, setSelectedTickers],
    );

    const removeTicker = (symbol: string) => {
        setSelectedTickers(selectedTickers.filter((ticker) => ticker !== symbol));
    };

    useEffect(() => {
        setActiveIndex(0);
    }, [search]);

    const downHandler = useCallback(
        (ev: React.KeyboardEvent<HTMLInputElement>) => {
            if (!open) {
                return;
            }

            if (ev.key === 'Escape') {
                inputRef.current?.blur();
                setSearch('');
                setOpen(false);
            }

            if (ev.key === 'Enter') {
                ev.preventDefault();

                if (matches.length === 0 || activeIndex === matches.length) {
                    search && addTicker(search);
                } else {
                    addTicker(matches[activeIndex].tickerSymbol);
                }
            }

            if (ev.key === 'ArrowDown') {
                setActiveIndex((current) => {
                    const index = Math.min(current + 1, matches.length ?? 0);

                    if (
                        context.refs.floating.current &&
                        Math.max(0, (index + 1) * ITEM_HEIGHT) > context.refs.floating.current?.scrollTop + 300
                    ) {
                        context.refs.floating.current?.scrollTo({
                            top: Math.max(0, (index - 11) * ITEM_HEIGHT),
                        });
                    }

                    return index;
                });
            }
            if (ev.key === 'ArrowUp') {
                setActiveIndex((current) => {
                    const index = Math.max(current - 1, 0);

                    const scrollRequiredForItem = Math.max(0, index * ITEM_HEIGHT);
                    if (
                        context.refs.floating.current &&
                        scrollRequiredForItem < context.refs.floating.current?.scrollTop
                    ) {
                        context.refs.floating.current?.scrollTo({ top: scrollRequiredForItem });
                    }

                    return index;
                });
            }
        },
        [activeIndex, addTicker, context.refs.floating, matches, open, search],
    );

    return (
        <>
            <VerticalLayout $gap={4}>
                <FormLabel>{label}</FormLabel>
                <Container $gap={8} ref={refs.setReference} onClick={() => inputRef.current?.focus()} $rowCenter>
                    {selectedTickers?.map((ticker) => (
                        <TickerTag key={ticker} $rowCenter $gap={2} onClick={(ev) => ev.stopPropagation()}>
                            <Text helper>{ticker}</Text>
                            <TickerRemove
                                size="micro"
                                color={themeDefault.text.negative}
                                onClick={() => removeTicker(ticker)}
                            />
                        </TickerTag>
                    ))}
                    <StyledInput
                        ref={inputRef}
                        onFocus={() => setOpen(true)}
                        onChange={(ev) => setSearch(sanitizeText(ev.target.value))}
                        value={search}
                        placeholder="Ticker(s)"
                        onKeyDown={downHandler}
                    />
                </Container>
                <InputDescription errorMessage={error?.message} />
            </VerticalLayout>

            {open && (
                <Dropdown
                    ref={refs.setFloating}
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                    }}
                >
                    {matches.map((stockInfo, index) => (
                        <ListItem
                            key={stockInfo.tickerSymbol}
                            role="presentation"
                            onClick={() => addTicker(stockInfo.tickerSymbol)}
                            onMouseMove={() => setActiveIndex(index)}
                            $active={index === activeIndex}
                        >
                            <Text helper>
                                {stockInfo.tickerSymbol} - {stockInfo.companyName}
                            </Text>
                        </ListItem>
                    ))}
                    <PlaceholderListItem
                        onClick={() => search && addTicker(search)}
                        onMouseMove={() => setActiveIndex(matches.length)}
                        $active={activeIndex === matches.length}
                    >
                        <Text helper>Add custom ticker</Text>
                    </PlaceholderListItem>
                </Dropdown>
            )}
        </>
    );
};

const Container = styled(HorizontalLayout)`
    border-bottom: 1px solid ${({ theme }) => theme.border.dark};
    padding-bottom: 4px;
    flex-wrap: wrap;
`;

const Dropdown = styled(Card)`
    overflow-y: auto;
    max-height: 300px;
    width: 500px;
    box-shadow: 0px 0px 10px 2px ${({ theme }) => theme.border.light};
    z-index: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const ListItem = styled.div<{ $active: boolean }>`
    cursor: pointer;
    padding: 0 8px;
    height: ${ITEM_HEIGHT}px;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    ${({ $active, theme }) => $active && `background: ${theme.container.secondaryBackground};`}
`;

const PlaceholderListItem = styled(ListItem)`
    border-bottom: 1px solid ${({ theme }) => theme.border.light};
`;

const TickerTag = styled(HorizontalLayout)`
    border: 1px solid ${({ theme }) => theme.border.light};
    border-radius: 3px;
    padding: 0 4px;
    height: 22px;
`;

const TickerRemove = styled(IconClose)`
    cursor: pointer;
`;

const StyledInput = styled(Input)`
    border: 0;
    outline: 0;
    width: 80px;
    padding: 4px;
`;
